import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ChatIcon from '@mui/icons-material/Chat';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

const LandingPage = () => {
  const navigate = useNavigate();

  // Toggles for each chat panel
  const [showOpenAIChat, setShowOpenAIChat] = useState(false);
  const [showRagChat, setShowRagChat] = useState(false);

  // OpenAI prod model state
  const [openAIUserMessage, setOpenAIUserMessage] = useState('');
  const [openAIChatResponse] = useState('');
  const [openAILoading, setOpenAILoading] = useState(false);

  // RAG (Pinecone + Llama) state
  const [ragUserMessage, setRagUserMessage] = useState('');
  const [ragChatResponse, setRagChatResponse] = useState('');
  const [ragLoading, setRagLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [ragHistory, setRagHistory] = useState([]);

  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const image = 'assets/jm_pic01.jpg';

  // Handler to show/hide the RAG chat panel
  const handleRagChatToggle = () => {
    setShowRagChat(prev => {
      if (prev) setRagChatResponse('');
      return !prev;
    });
    setShowOpenAIChat(false);
  };

  // Format response HTML (headers, bold, lists)
  const formatResponse = (response) => {
    response = response.replace(/###\s*(.*?)\s*###/g, '<h3 style="margin-top:16px;font-weight:bold;">$1</h3>');
    response = response.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    response = response.replace(/(\d+\.\s)/g, '<br/><br/>$1');
    response = response.replace(/-\s/g, '<br/>• ');
    return response;
  };

    const handleSendOpenAIMessage = async () => {
        const userText = openAIUserMessage.trim();
        if (!userText) return;

        // Add the user’s message to history immediately
        setHistory(h => [...h, { from: 'user', text: userText }]);
        setOpenAIUserMessage('');
        setOpenAILoading(true);

        try {
            const res = await fetch(`${baseURL}/search_openai`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: userText })
            });
            const { results } = await res.json();
            setHistory(h => [...h, { from: 'assistant', text: results }]);
        } catch (err) {
            console.error(err);
            setHistory(h => [...h, { from: 'assistant', text: 'Oops, something went wrong.' }]);
        } finally {
            setOpenAILoading(false);
        }
    };

  // Handler for RAG pipeline
  const handleSendRagMessage = async () => {
    const userText = ragUserMessage.trim();
    if (!userText) return;
  
    // 1) push the user message
    setRagHistory(h => [...h, { from: 'user', text: userText }]);
    setRagUserMessage('');
    setRagLoading(true);
  
    try {
      const res = await fetch(`${baseURL}/query_rag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: userText }),
      });
      const { results } = await res.json();
      const formatted = formatResponse(results || "Sorry, I couldn't find any relevant info.");
  
      // 2) push the assistant reply
      setRagHistory(h => [...h, { from: 'assistant', text: formatted }]);
    } catch (e) {
      console.error(e);
      setRagHistory(h => [...h, { from: 'assistant', text: 'There was an error processing your request.' }]);
    } finally {
      setRagLoading(false);
    }
  };
  

  // Prevent empty sends on Enter
  const handleKeyDown = (e, fn) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      fn();
    }
  };

  return (
    <Box
      sx={{
        backgroundImage: 'linear-gradient(to bottom, #120222, #095c79)',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4,
        position: 'relative',
      }}
    >
      <Stack spacing={4} sx={{ maxWidth: 600, textAlign: 'center', alignItems: 'center' }}>
        <Box
          component="img"
          src={image}
          sx={{
            width: 150,
            height: 150,
            objectFit: 'cover',
            borderRadius: '50%',
            border: '5px solid white',
          }}
        />
        <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
          Hi there, I'm Jia Ming!
        </Typography>
        <Typography variant="h6" sx={{ color: '#B0BEC5' }}>
          I am a passionate developer with an immense interest in ML/AI.
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="contained" onClick={() => navigate('/projects')} sx={{ backgroundColor: '#1E88E5' }}>Projects</Button>
          <Button variant="contained" onClick={() => navigate('/about')} sx={{ backgroundColor: '#1E88E5' }}>About Me</Button>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ backgroundColor: '#1E88E5' }}>Contact</Button>
          <Button variant="contained" onClick={() => navigate('/wildside')} sx={{ backgroundColor: '#1E88E5' }}>Wild Side</Button>
        </Stack>

        <Typography variant="h7" sx={{ color: '#B0BEC5' }}>
          Feeling bored? Find out more about me with my RAG chatbot below!
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
          {/* OpenAI Prod Chat */}
          {/* <Box textAlign="center">
            <IconButton
              onClick={handleOpenAIChatToggle}
              sx={{
                color: 'white',
                backgroundColor: '#1E88E5',
                '&:hover': { backgroundColor: '#1565C0' },
              }}
            >
              <ChatIcon />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#B0BEC5', mt: 1, ml: 1 }}>
              Ask me anything about ME!
            </Typography>
          </Box> */}

          {/* RAG Chat */}
          <Box textAlign="center">
            <IconButton
              onClick={handleRagChatToggle}
              sx={{
                color: 'white',
                backgroundColor: '#43A047',
                '&:hover': { backgroundColor: '#2E7D32' },
              }}
            >
              <ChatIcon />
            </IconButton>
            <Typography variant="caption" sx={{ color: '#B0BEC5', mt: 1, ml: 1 }}>
              Ask about me with my RAG system
            </Typography>
          </Box>
        </Stack>

        {/* OpenAI prod chat panel */}
        {showOpenAIChat && (
          <Box sx={{ backgroundColor: '#1E1E1E', p: 3, borderRadius: 3, boxShadow: '0px 4px 12px rgba(0,0,0,0.3)', mt: 2 }}>
            <Typography variant="body1" sx={{ color: '#ECEFF1', fontWeight: 'bold' }}>
              Hi there! How can I help you today? (API Model)
            </Typography>
            {history.map((msg, i) => (
                <Typography
                key={i}
                sx={{
                    textAlign: msg.from === 'user' ? 'right' : 'left',
                    color: msg.from === 'user' ? '#90CAF9' : '#CE93D8',
                    mb: 1
                }}
                >
                {msg.text}
                </Typography>
            ))}
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Ask a question about me!"
              value={openAIUserMessage}
              onChange={e => setOpenAIUserMessage(e.target.value)}
              onKeyDown={e => handleKeyDown(e, handleSendOpenAIMessage)}
              sx={{
                mt: 2,
                backgroundColor: '#37474F',
                '& .MuiOutlinedInput-root': {
                  color: '#ECEFF1',
                  '& fieldset': { borderColor: '#607D8B' },
                },
              }}
            />
            <Button onClick={handleSendOpenAIMessage} sx={{ mt: 2, backgroundColor: '#1E88E5', color: 'white' }}>
              Send
            </Button>
            {openAILoading ? (
              <CircularProgress size={24} sx={{ color: '#B0BEC5', mt: 2 }} />
            ) : (
              openAIChatResponse && (
                <Typography
                  variant="body2"
                  sx={{ color: '#B0BEC5', mt: 2, fontStyle: 'italic', backgroundColor: '#2E3B44', p: 1.5, borderRadius: 2 }}
                  dangerouslySetInnerHTML={{ __html: openAIChatResponse }}
                />
              )
            )}
          </Box>
        )}

        {/* RAG chat panel */}
        {showRagChat && (
          <Box sx={{ backgroundColor: '#1E1E1E', p: 3, borderRadius: 3, boxShadow: '0px 4px 12px rgba(0,0,0,0.3)', mt: 2 }}>
            <Typography variant="body1" sx={{ color: '#ECEFF1', fontWeight: 'bold' }}>
              Hi there! This uses Pinecone+RAG pipeline
            </Typography>
            {ragHistory.map((msg,i) => (
            <Typography
                key={i}
                sx={{
                textAlign: msg.from==='user' ? 'right' : 'left',
                color: msg.from==='user' ? '#90CAF9' : '#43A047',
                mb: 1
                }}
                dangerouslySetInnerHTML={{ __html: msg.text }}
            />
            ))}
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Ask a question about me!"
              value={ragUserMessage}
              onChange={e => setRagUserMessage(e.target.value)}
              onKeyDown={e => handleKeyDown(e, handleSendRagMessage)}
              sx={{
                mt: 2,
                backgroundColor: '#37474F',
                '& .MuiOutlinedInput-root': {
                  color: '#ECEFF1',
                  '& fieldset': { borderColor: '#607D8B' },
                },
              }}
            />
            <Button onClick={handleSendRagMessage} sx={{ mt: 2, backgroundColor: '#43A047', color: 'white' }}>
              Send
            </Button>
            {ragLoading ? (
              <CircularProgress size={24} sx={{ color: '#B0BEC5', mt: 2 }} />
            ) : (
              ragChatResponse && (
                <Typography
                  variant="body2"
                  sx={{ color: '#B0BEC5', mt: 2, fontStyle: 'italic', backgroundColor: '#2E3B44', p: 1.5, borderRadius: 2 }}
                  dangerouslySetInnerHTML={{ __html: ragChatResponse }}
                />
              )
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default LandingPage;